// Shared helpers to resolve a location/zone id to GPS coordinates.
// Zones are hierarchical (children / subzones). A sub-location without its own
// GPS falls back to the nearest ancestor zone that has GPS, so every incident,
// exercise or scenario placed at any depth still shows on the map.

const nodeCoords = (node) => {
  if (node?.gps_is_inherited || String(node?.gps_coordinate_source || '').toLowerCase() === 'inherited') {
    return null;
  }
  const lat = node?.gps_latitude ?? node?.lat;
  const lng = node?.gps_longitude ?? node?.lng;
  if (lat != null && lng != null && !Number.isNaN(Number(lat)) && !Number.isNaN(Number(lng))) {
    return { lat: Number(lat), lng: Number(lng) };
  }
  return null;
};

const nodeName = (node) => node?.zone_name || node?.name || node?.label || '';

// Flatten hierarchical zones into id -> { node, parentId, name }.
export const buildZoneIndex = (zones) => {
  const index = new Map();
  const walk = (list, parentId) => {
    (list || []).forEach((z) => {
      if (!z || !z.id) return;
      index.set(z.id, { node: z, parentId: z.parent_id || parentId, name: nodeName(z) });
      walk(z.children, z.id);
      walk(z.subzones, z.id);
    });
  };
  walk(zones, null);
  return index;
};

// Resolve coords for a location id, walking up to parent zones when the node
// (or its descendants) has no GPS. Returns { lat, lng, name, matchedName } | null.
export const resolveZoneCoords = (index, locId) => {
  let cur = index.get(locId);
  const ownName = cur?.name || '';
  const selectedId = locId;
  let guard = 0;
  while (cur && guard < 25) {
    const c = nodeCoords(cur.node);
    if (c) return {
      ...c,
      name: ownName || cur.name,
      matchedName: cur.name,
      source: cur.node?.id === selectedId ? (cur.node?.gps_coordinate_source || 'location') : 'inherited',
      inheritedFrom: cur.node?.id === selectedId ? null : { id: cur.node?.id, name: cur.name },
    };
    cur = cur.parentId ? index.get(cur.parentId) : null;
    guard += 1;
  }
  return null;
};

// Display name (full label) for a location id.
export const resolveZoneName = (index, locId) => index.get(locId)?.name || '';

// Resolve the first plottable coords for an incident/exercise/scenario.
// The selected Location & Zone is the reported location and must match the
// incident-page map widget. Captured device GPS is only a fallback for Unknown
// locations or zones that do not have plottable coordinates.
export const resolveIncidentCoords = (index, inc) => {
  const projected = inc?.map_projection?.coordinates || inc?.map_projection?.official_coordinates;
  const projectedLat = projected?.latitude ?? projected?.lat;
  const projectedLng = projected?.longitude ?? projected?.lng;
  if (projectedLat != null && projectedLng != null && !Number.isNaN(Number(projectedLat)) && !Number.isNaN(Number(projectedLng))) {
    return {
      lat: Number(projectedLat),
      lng: Number(projectedLng),
      name: inc?.map_projection?.location_name || '',
      source: inc?.map_projection?.coordinate_source,
      official: !!inc?.map_projection?.official,
      inheritedFrom: inc?.map_projection?.inherited_from || null,
    };
  }
  const locIds = (Array.isArray(inc?.location) ? inc.location : [inc?.location]).filter(Boolean);
  for (const id of locIds) {
    const c = resolveZoneCoords(index, id);
    if (c) return c;
  }

  const lat = inc?.gps_coordinates?.latitude ?? inc?.gps_coordinates?.lat ?? inc?.gps_latitude;
  const lng = inc?.gps_coordinates?.longitude ?? inc?.gps_coordinates?.lng ?? inc?.gps_longitude;
  if (lat != null && lng != null && !Number.isNaN(Number(lat)) && !Number.isNaN(Number(lng))) {
    const firstId = locIds[0];
    return { lat: Number(lat), lng: Number(lng), name: resolveZoneName(index, firstId) || '' };
  }
  return null;
};
